import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  ArrowUp,
  Bot,
  Sparkles,
  Copy,
  Check,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { toast } from "react-hot-toast";
import documentSearchService, {
  ChatSessionMessage,
} from "../../services/document-search.service";
import ChatSidebar, { ChatSidebarRef } from "./ChatSidebar";
import MarkdownRenderer from "./MarkdownRenderer";

const POLL_INTERVAL_MS = 2000;

function formatTime(dateString: string) {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── MessageList ────────────────────────────────────────────────────────────
// Isolated with React.memo so that typing in the input box (which changes
// inputMessage state in the parent) never triggers a re-render here.

interface MessageListProps {
  messages: ChatSessionMessage[];
  isProcessing: boolean;
  retryMessage: string | null;
  onRetry: (content: string) => void;
}

const MessageList = React.memo<MessageListProps>(
  ({ messages, isProcessing, retryMessage, onRetry }) => {
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isProcessing]);

    const handleCopy = async (messageId: string, content: string) => {
      try {
        await navigator.clipboard.writeText(content);
        setCopiedMessageId(messageId);
        toast.success("Copied!");
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch {
        toast.error("Failed to copy to clipboard");
      }
    };

    return (
      <div className="px-5 py-4 space-y-5">
        {messages.map((msg) => (
          <div key={msg._id}>
            {msg.role === "user" ? (
              <div className="flex justify-end">
                <div className="max-w-[75%]">
                  <div className="bg-blue-600 text-white rounded-lg rounded-br-md px-4 py-2.5 text-sm leading-relaxed">
                    <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                  </div>
                  <div className="flex justify-end mt-1 pr-1">
                    <span className="text-[10px] text-slate-400">
                      {formatTime(msg.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-2.5">
                  <div className="relative w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center overflow-hidden">
                    <Bot className="absolute w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 leading-relaxed">
                    <MarkdownRenderer content={msg.content} />
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-slate-400">
                      {formatTime(msg.createdAt)}
                    </span>
                    <button
                      onClick={() => handleCopy(msg._id, msg.content)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                        copiedMessageId === msg._id
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
                      }`}
                      title="Copy response"
                    >
                      {copiedMessageId === msg._id ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {isProcessing && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <div className="ai-orb-sm"></div>
              </div>
            </div>
            <div className="flex-1 pt-0.5 space-y-2.5">
              <div className="h-3 rounded-md w-[90%] ai-shimmer"></div>
              <div className="h-3 rounded-md w-[70%] ai-shimmer" style={{ animationDelay: "0.2s" }}></div>
              <div className="h-3 rounded-md w-[50%] ai-shimmer" style={{ animationDelay: "0.4s" }}></div>
              <div className="relative h-4 overflow-hidden mt-1">
                <span className="text-xs text-indigo-500 font-medium ai-text-fade">
                  Analyzing documents...
                </span>
                <span className="text-xs text-indigo-500 font-medium ai-text-fade" style={{ animationDelay: "3s" }}>
                  Generating response...
                </span>
                <span className="text-xs text-indigo-500 font-medium ai-text-fade" style={{ animationDelay: "6s" }}>
                  Almost there...
                </span>
              </div>
            </div>
          </div>
        )}

        {!isProcessing && retryMessage && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-2.5">
              <div className="w-7 h-7 rounded-md bg-slate-200 flex items-center justify-center">
                <Bot className="w-4 h-4 text-slate-400" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-400 italic">
                Response could not be generated.
              </p>
              <button
                onClick={() => onRetry(retryMessage)}
                className="mt-1.5 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
              >
                <ArrowUp className="w-3 h-3 rotate-45" />
                Retry
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    );
  },
);

// ─── AIChatTab ───────────────────────────────────────────────────────────────

interface AIChatTabProps {
  documentStoreId: string;
}

const AIChatTab: React.FC<AIChatTabProps> = ({ documentStoreId }) => {
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatSessionMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sessionTitle, setSessionTitle] = useState("New Chat");
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sidebarRef = useRef<ChatSidebarRef>(null);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeJobsRef = useRef<Record<string, string>>({});
  const activeSessionRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    stopPolling();
    setActiveSessionId(null);
    setMessages([]);
    setSessionTitle("New Chat");
    setIsProcessing(false);
    setRetryMessage(null);
  }, [documentStoreId, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    activeSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  const pollJobStatus = useCallback(
    (sessionId: string, jobId: string, lastUserMsgContent: string) => {
      activeJobsRef.current[sessionId] = jobId;

      pollingRef.current = setTimeout(async () => {
        if (activeSessionRef.current !== sessionId) {
          delete activeJobsRef.current[sessionId];
          return;
        }

        try {
          const jobResp = await documentSearchService.getMessageJobStatus(
            sessionId,
            jobId,
          );

          if (activeSessionRef.current !== sessionId) {
            delete activeJobsRef.current[sessionId];
            return;
          }

          if (!jobResp.success) {
            delete activeJobsRef.current[sessionId];
            toast.error("Failed to get AI response");
            setIsProcessing(false);
            return;
          }

          const job = jobResp.data;

          if (job.status === "processing") {
            pollJobStatus(sessionId, jobId, lastUserMsgContent);
            return;
          }

          delete activeJobsRef.current[sessionId];
          setIsProcessing(false);

          if (job.status === "done") {
            setRetryMessage(null);
            const assistantMsg: ChatSessionMessage = {
              _id: job.assistant_message_id ?? `asst-${Date.now()}`,
              role: "assistant",
              content: job.assistant_content ?? "",
              is_deleted: false,
              createdAt: job.updatedAt ?? new Date().toISOString(),
              updatedAt: job.updatedAt ?? new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMsg]);
            if (sidebarRef.current) {
              sidebarRef.current.refreshSessions();
            }
          } else {
            setRetryMessage(lastUserMsgContent);
            toast.error(job.error_message ?? "AI failed to generate a response");
          }
        } catch (error: any) {
          delete activeJobsRef.current[sessionId];
          toast.error(
            error.response?.data?.message ?? "Failed to get AI response",
          );
          setIsProcessing(false);
        }
      }, POLL_INTERVAL_MS);
    },
    [],
  );

  const loadSession = useCallback(async (sessionId: string) => {
    try {
      const response =
        await documentSearchService.getChatSessionById(sessionId);
      if (response.success) {
        const loadedMessages = response.data.messages;
        setMessages(loadedMessages);
        setSessionTitle(response.data.session_title);
        setActiveSessionId(sessionId);
        setRetryMessage(null);

        const lastMsg = loadedMessages[loadedMessages.length - 1];
        if (lastMsg?.role === "user") {
          // Prefer in-memory job id (same page session), fall back to BE-provided one
          const pendingJobId =
            activeJobsRef.current[sessionId] ?? response.data.pending_job_id;

          if (pendingJobId) {
            setIsProcessing(true);
            pollJobStatus(sessionId, pendingJobId, lastMsg.content);
          } else {
            // No job id known — show retry option so the user can resend
            setRetryMessage(lastMsg.content);
          }
        } else {
          delete activeJobsRef.current[sessionId];
        }
      }
    } catch (error) {
      console.error("Error loading session:", error);
      toast.error("Failed to load chat session");
    }
  }, [pollJobStatus]);

  const handleNewChat = useCallback(() => {
    stopPolling();
    setActiveSessionId(null);
    setMessages([]);
    setSessionTitle("New Chat");
    setInputMessage("");
    setIsProcessing(false);
    setRetryMessage(null);
  }, [stopPolling]);

  const handleSessionSelect = useCallback(
    (sessionId: string) => {
      if (sessionId !== activeSessionId) {
        stopPolling();
        setIsProcessing(false);
        loadSession(sessionId);
      }
    },
    [activeSessionId, loadSession, stopPolling],
  );

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isProcessing) return;

    const messageText = inputMessage.trim();
    setInputMessage("");
    setIsProcessing(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    const tempUserMsg: ChatSessionMessage = {
      _id: `temp-user-${Date.now()}`,
      role: "user",
      content: messageText,
      is_deleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      let sessionId = activeSessionId;

      if (!sessionId) {
        const createResp =
          await documentSearchService.createChatSession(documentStoreId);
        if (createResp.success) {
          sessionId = createResp.data._id;
          setActiveSessionId(sessionId);
        } else {
          toast.error("Failed to create chat session");
          setMessages((prev) => prev.filter((m) => m._id !== tempUserMsg._id));
          setIsProcessing(false);
          return;
        }
      }

      const response = await documentSearchService.sendChatMessage(
        sessionId!,
        messageText,
        documentStoreId,
      );

      if (response.success) {
        setMessages((prev) => {
          const withoutTemp = prev.filter((m) => m._id !== tempUserMsg._id);
          return [...withoutTemp, response.data.user_message];
        });

        if (response.data.session_title) {
          setSessionTitle(response.data.session_title);
        }

        pollJobStatus(sessionId!, response.data.job_id, messageText);
      } else {
        toast.error("Failed to send message");
        setMessages((prev) => prev.filter((m) => m._id !== tempUserMsg._id));
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Error in AI chat:", error);
      toast.error(error.response?.data?.message || "Failed to send message");
      setMessages((prev) => prev.filter((m) => m._id !== tempUserMsg._id));
      setIsProcessing(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + "px";
  };

  const handleRetry = useCallback((content: string) => {
    setRetryMessage(null);
    setInputMessage(content);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const handleSuggestionClick = (text: string) => {
    setInputMessage(text);
    textareaRef.current?.focus();
  };

  const hasConversation = messages.length > 0 || isProcessing || !!retryMessage;

  const inputBox = (
    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-2.5 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
      <textarea
        ref={textareaRef}
        value={inputMessage}
        onChange={handleTextareaChange}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
        placeholder="Ask anything about your documents..."
        disabled={isProcessing}
        rows={1}
        className="flex-1 bg-transparent text-sm text-slate-800 placeholder-slate-400 resize-none outline-none disabled:opacity-60 leading-relaxed"
        style={{ minHeight: "24px", maxHeight: "150px" }}
      />
      <button
        onClick={handleSendMessage}
        disabled={isProcessing || !inputMessage.trim()}
        className="flex-shrink-0 p-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all"
        title="Send message"
      >
        {isProcessing ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <ArrowUp className="w-4 h-4" />
        )}
      </button>
    </div>
  );

  return (
    <div className="flex bg-white overflow-hidden h-full border border-slate-200">
      {/* Sidebar */}
      {isSidebarOpen && (
        <div className="w-60 flex-shrink-0 overflow-hidden">
          <ChatSidebar
            ref={sidebarRef}
            documentStoreId={documentStoreId}
            activeSessionId={activeSessionId}
            onSessionSelect={handleSessionSelect}
            onNewChat={handleNewChat}
          />
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-slate-50">
        {/* Chat Header */}
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-white">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 transition-colors"
            title={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            {isSidebarOpen ? (
              <PanelLeftClose className="w-4 h-4" />
            ) : (
              <PanelLeftOpen className="w-4 h-4" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">
              {sessionTitle}
            </h3>
          </div>
        </div>

        {hasConversation ? (
          /* ===== CONVERSATION MODE — messages scroll, input fixed bottom ===== */
          <>
            <div className="flex-1 overflow-y-auto min-h-0 chat-scrollbar">
              <MessageList
                messages={messages}
                isProcessing={isProcessing}
                retryMessage={retryMessage}
                onRetry={handleRetry}
              />
            </div>

            {/* Input pinned to bottom */}
            <div className="flex-shrink-0 bg-white border-t border-slate-200 px-5 py-2">
              <div>{inputBox}</div>
            </div>
          </>
        ) : (
          /* ===== WELCOME MODE ===== */
          <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-0">
            <div className="w-full max-w-2xl">
              {/* Hero */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 mb-5">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  How can I help you today?
                </h2>
                <p className="text-sm text-slate-500 leading-relaxed max-w-md mx-auto">
                  Ask me anything about your uploaded documents. I'll search
                  through your files and provide accurate answers.
                </p>
              </div>

              {/* Suggestion Cards */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  {
                    icon: "📄",
                    title: "Summarize",
                    desc: "Get key points from your documents",
                  },
                  {
                    icon: "🔍",
                    title: "Find Info",
                    desc: "Search for specific details",
                  },
                  {
                    icon: "📊",
                    title: "Compare",
                    desc: "Analyze differences between files",
                  },
                  {
                    icon: "📅",
                    title: "Extract Data",
                    desc: "Pull dates, numbers & entities",
                  },
                ].map((item) => (
                  <button
                    key={item.title}
                    onClick={() => handleSuggestionClick(item.desc)}
                    className="group flex items-start gap-3 text-left p-4 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-md transition-all"
                  >
                    <span className="text-xl mt-0.5">{item.icon}</span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-700">
                        {item.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 group-hover:text-blue-600">
                        {item.desc}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Input */}
              <div className="w-full">{inputBox}</div>
              <p className="text-[11px] text-slate-400 mt-2 text-center">
                Press Enter to send · Shift+Enter for new line
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIChatTab;
